import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  IconButton,
  Badge,
  Checkbox,
  Tooltip,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import ClaimEditModal from './ClaimEditModal';
import ClaimEvidenceModal from './ClaimEvidenceModal';
import { useClaimEvidenceService } from '../../services/claimEvidenceService';
import { useClaimService } from '../../services/claimService';

interface Claim {
  id?: number;
  externalClaimId?: string;
  dateOfServiceFrom?: string;
  deliveryMethod?: string;
  status?: string;
  readyForSync?: boolean;
  referralId?: number;
  clientLabel?: string;
  serviceType?: string;
  assignedSpecialistName?: string;
}

interface ClaimsModalProps {
  open: boolean;
  onClose: () => void;
  claims: Claim[];
  viewOnly?: boolean;
  getCatalogDescription: (catalogType: string, code: string) => string;
  onClaimUpdate: () => void;
}

interface ClaimGroup {
  key: string;
  clientLabel: string;
  serviceLabel: string;
  specialistLabel: string;
  claims: Claim[];
}

const ClaimsModal: React.FC<ClaimsModalProps> = ({
  open,
  onClose,
  claims,
  viewOnly = false,
  getCatalogDescription,
  onClaimUpdate
}) => {
  const [claimEditModalOpen, setClaimEditModalOpen] = useState(false);
  const [selectedClaimForEdit, setSelectedClaimForEdit] = useState<Claim | null>(null);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [selectedClaimForEvidence, setSelectedClaimForEvidence] = useState<Claim | null>(null);
  const [evidenceCounts, setEvidenceCounts] = useState<Record<number, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [selectedClaimIds, setSelectedClaimIds] = useState<Set<number>>(new Set());
  const [confirmReadyOpen, setConfirmReadyOpen] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);
  const [markReadyError, setMarkReadyError] = useState('');

  const { getClaimEvidences } = useClaimEvidenceService();
  const { markClaimsReadyForSync } = useClaimService();

  const hasSpecialistAssigned = (claim: Claim) =>
    Boolean(claim.assignedSpecialistName?.trim());

  const BLOCKED_NO_SPECIALIST =
    'Asigna un especialista al servicio antes de gestionar citas y evidencias.';

  const isEligibleForMarkReady = (claim: Claim) =>
    Boolean(claim.id) &&
    !claim.readyForSync &&
    !claim.externalClaimId &&
    hasSpecialistAssigned(claim);

  const claimsWithoutSpecialist = useMemo(
    () => claims.some((c) => !hasSpecialistAssigned(c)),
    [claims]
  );

  const eligibleClaims = useMemo(
    () => claims.filter(isEligibleForMarkReady),
    [claims]
  );

  useEffect(() => {
    if (!open) {
      setSelectedClaimIds(new Set());
      setConfirmReadyOpen(false);
      setMarkReadyError('');
      return;
    }
    // Limpiar selección de citas que ya no son elegibles
    setSelectedClaimIds((prev) => {
      const next = new Set<number>();
      prev.forEach((id) => {
        if (claims.some((c) => c.id === id && isEligibleForMarkReady(c))) {
          next.add(id);
        }
      });
      return next;
    });
  }, [open, claims]);

  const handleEditClaim = (claim: Claim) => {
    if (!hasSpecialistAssigned(claim)) return;
    setSelectedClaimForEdit(claim);
    setClaimEditModalOpen(true);
  };

  const handleClaimUpdateSuccess = () => {
    setClaimEditModalOpen(false);
    setSelectedClaimForEdit(null);
    onClaimUpdate();
  };

  const handleViewEvidence = (claim: Claim) => {
    if (!claim.id) return;
    setSelectedClaimForEvidence(claim);
    setEvidenceModalOpen(true);
  };

  const handleEvidenceUpdate = () => {
    if (selectedClaimForEvidence?.id) {
      loadEvidenceCount(selectedClaimForEvidence.id);
    }
    onClaimUpdate();
  };

  useEffect(() => {
    if (open && claims.length > 0) {
      loadAllEvidenceCounts();
    }
  }, [open, claims]);

  const loadEvidenceCount = async (claimId: number) => {
    try {
      const evidences = await getClaimEvidences(claimId);
      setEvidenceCounts((prev) => ({
        ...prev,
        [claimId]: evidences.length
      }));
    } catch (error) {
      console.error(`Error loading evidence count for claim ${claimId}:`, error);
      setEvidenceCounts((prev) => ({
        ...prev,
        [claimId]: 0
      }));
    }
  };

  const loadAllEvidenceCounts = async () => {
    setLoadingCounts(true);
    const counts: Record<number, number> = {};

    try {
      await Promise.all(
        claims
          .filter((cl) => cl.id)
          .map(async (claim) => {
            try {
              const evidences = await getClaimEvidences(claim.id!);
              counts[claim.id!] = evidences.length;
            } catch (error) {
              console.error(`Error loading evidence count for claim ${claim.id}:`, error);
              counts[claim.id!] = 0;
            }
          })
      );
      setEvidenceCounts(counts);
    } catch (error) {
      console.error('Error loading evidence counts:', error);
    } finally {
      setLoadingCounts(false);
    }
  };

  const formatClientLabel = (claim: Claim) => claim.clientLabel?.trim() || '—';

  const formatServiceLabel = (claim: Claim) =>
    getCatalogDescription('PRODUCT', claim.serviceType || '') || claim.serviceType || '—';

  const formatSpecialistLabel = (claim: Claim) =>
    claim.assignedSpecialistName?.trim() || 'Sin asignar';

  const claimGroups = useMemo((): ClaimGroup[] => {
    const groups = new Map<string, ClaimGroup>();

    claims.forEach((claim) => {
      const clientLabel = formatClientLabel(claim);
      const serviceLabel = formatServiceLabel(claim);
      const specialistLabel = formatSpecialistLabel(claim);
      const groupKey = [
        claim.referralId ?? 'no-ref',
        clientLabel,
        claim.serviceType ?? 'no-service',
        specialistLabel
      ].join('|');

      const existing = groups.get(groupKey);
      if (existing) {
        existing.claims.push(claim);
        return;
      }

      groups.set(groupKey, {
        key: groupKey,
        clientLabel,
        serviceLabel,
        specialistLabel,
        claims: [claim]
      });
    });

    return Array.from(groups.values());
  }, [claims, getCatalogDescription]);

  const toggleClaimSelection = (claimId: number, checked: boolean) => {
    setSelectedClaimIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(claimId);
      else next.delete(claimId);
      return next;
    });
  };

  const handleConfirmMarkReady = async () => {
    const ids = Array.from(selectedClaimIds);
    if (ids.length === 0) return;

    setMarkingReady(true);
    setMarkReadyError('');
    try {
      const response = await markClaimsReadyForSync(ids);
      if (!response.success) {
        setMarkReadyError(response.error || 'No se pudieron marcar las citas.');
        return;
      }
      setConfirmReadyOpen(false);
      setSelectedClaimIds(new Set());
      onClaimUpdate();
    } catch (err: unknown) {
      setMarkReadyError(
        err instanceof Error ? err.message : 'No se pudieron marcar las citas.'
      );
    } finally {
      setMarkingReady(false);
    }
  };

  const renderClaimRow = (cl: Claim, idx: number) => {
    const channelized = hasSpecialistAssigned(cl);
    const mutationsBlocked = !viewOnly && !channelized;
    const alreadyReady = Boolean(cl.readyForSync);
    const alreadySynced = Boolean(cl.externalClaimId);
    const eligible = isEligibleForMarkReady(cl);
    const selected = Boolean(cl.id && selectedClaimIds.has(cl.id));

    return (
      <TableRow key={`${cl.id || cl.externalClaimId || idx}`} hover>
        <TableCell sx={{ py: 1.5 }}>
          {cl.dateOfServiceFrom
            ? new Date(
                cl.dateOfServiceFrom + (cl.dateOfServiceFrom.length === 10 ? 'T12:00:00' : '')
              ).toLocaleDateString('es-MX')
            : '—'}
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          {getCatalogDescription('DELIVERY_METHOD', cl.deliveryMethod || '') || '—'}
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          <Chip
            label={cl.status || '—'}
            size="small"
            color={
              cl.status === 'SINCRONIZADO'
                ? 'success'
                : cl.status === 'PENDIENTE'
                  ? 'warning'
                  : 'default'
            }
          />
        </TableCell>
        {!viewOnly && (
          <>
            <TableCell align="center" sx={{ py: 1.5 }}>
              {alreadyReady || alreadySynced ? (
                <Tooltip
                  title={
                    alreadySynced
                      ? 'Esta cita ya está sincronizada'
                      : 'Enviado a Sync (no se puede deshacer)'
                  }
                >
                  <span>
                    <Checkbox checked disabled size="small" color="success" />
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    mutationsBlocked
                      ? BLOCKED_NO_SPECIALIST
                      : 'Seleccionar para marcar para Sync'
                  }
                >
                  <span>
                    <Checkbox
                      checked={selected}
                      disabled={!eligible || mutationsBlocked}
                      onChange={(e) => {
                        if (!cl.id || !eligible || mutationsBlocked) return;
                        toggleClaimSelection(cl.id, e.target.checked);
                      }}
                      size="small"
                      color="primary"
                    />
                  </span>
                </Tooltip>
              )}
            </TableCell>
            <TableCell sx={{ py: 1.5 }} align="center">
              {loadingCounts ? (
                <Typography variant="body2" color="text.secondary">
                  ...
                </Typography>
              ) : (
                <Badge badgeContent={evidenceCounts[cl.id || 0] || 0} color="primary" max={99}>
                  <AttachFileIcon fontSize="small" color="action" />
                </Badge>
              )}
            </TableCell>
            <TableCell sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={mutationsBlocked ? BLOCKED_NO_SPECIALIST : ''}>
                  <span>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => handleEditClaim(cl)}
                      startIcon={<AssignmentIcon />}
                      disabled={mutationsBlocked || alreadySynced}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Editar
                    </Button>
                  </span>
                </Tooltip>
                {cl.id && (
                  <Tooltip
                    title={
                      mutationsBlocked
                        ? `${BLOCKED_NO_SPECIALIST} Puedes consultar evidencias existentes en solo lectura.`
                        : ''
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => handleViewEvidence(cl)}
                        startIcon={<AttachFileIcon />}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Evidencias
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          </>
        )}
      </TableRow>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Citas del caso ({claims.length})
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!viewOnly && claimsWithoutSpecialist && claims.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {BLOCKED_NO_SPECIALIST}
            </Alert>
          )}
          {!viewOnly && eligibleClaims.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Selecciona las citas y usa «Marcar para Sync». Aparecerán en Sincronización
              ComPsych (aún no enviadas). Esta acción es irreversible.
            </Alert>
          )}
          {claims.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No hay citas asociadas a este caso.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ maxHeight: '500px', overflowY: 'auto', pr: 0.5 }}>
              {claimGroups.map((group) => (
                <Paper key={group.key} variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: 'grey.50',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Beneficiario: {group.clientLabel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Servicio: {group.serviceLabel} · Canalizado a: {group.specialistLabel}
                    </Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', py: 1.25 }}>Fecha de Servicio</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 1.25 }}>Método de Entrega</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 1.25 }}>Estado</TableCell>
                        {!viewOnly && (
                          <>
                            <TableCell sx={{ fontWeight: 'bold', py: 1.25 }} align="center">
                              Envío Sync
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 1.25 }} align="center">
                              Evidencias
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 1.25, width: '180px' }}>
                              Acciones
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.claims.map((cl, idx) => renderClaimRow(cl, idx))}
                    </TableBody>
                  </Table>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          {!viewOnly && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<SyncIcon />}
              disabled={selectedClaimIds.size === 0}
              onClick={() => {
                setMarkReadyError('');
                setConfirmReadyOpen(true);
              }}
            >
              Marcar para Sync
              {selectedClaimIds.size > 0 ? ` (${selectedClaimIds.size})` : ''}
            </Button>
          )}
          <Button onClick={onClose} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmReadyOpen}
        onClose={() => !markingReady && setConfirmReadyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar marcar para Sync</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción <strong>no se puede deshacer</strong>. Las citas aparecerán en
            Sincronización ComPsych; la sincronización real ocurre al enviarlas a ComPsych.
          </Alert>
          <Typography sx={{ mb: 1 }}>
            ¿Confirmas marcar {selectedClaimIds.size} cita
            {selectedClaimIds.size !== 1 ? 's' : ''} para Sync?
          </Typography>
          {markReadyError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setMarkReadyError('')}>
              {markReadyError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmReadyOpen(false)} disabled={markingReady}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void handleConfirmMarkReady()}
            disabled={markingReady}
            startIcon={markingReady ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
          >
            {markingReady ? 'Marcando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ClaimEditModal
        open={claimEditModalOpen}
        onClose={() => {
          setClaimEditModalOpen(false);
          setSelectedClaimForEdit(null);
        }}
        claim={selectedClaimForEdit}
        onSuccess={handleClaimUpdateSuccess}
      />

      {selectedClaimForEvidence?.id && (
        <ClaimEvidenceModal
          open={evidenceModalOpen}
          onClose={() => {
            setEvidenceModalOpen(false);
            setSelectedClaimForEvidence(null);
          }}
          claimId={selectedClaimForEvidence.id}
          clientLabel={selectedClaimForEvidence.clientLabel}
          serviceLabel={formatServiceLabel(selectedClaimForEvidence)}
          assignedSpecialistName={selectedClaimForEvidence.assignedSpecialistName}
          allowMutations={!viewOnly && hasSpecialistAssigned(selectedClaimForEvidence)}
          onEvidenceUpdate={handleEvidenceUpdate}
        />
      )}
    </>
  );
};

export default ClaimsModal;
